import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import mondaySdk from "monday-sdk-js";

const monday = mondaySdk();

interface MondayState {
  userId: number | null;
  accountId: number | null;
  currentBoard: number | null;
  itemIds: number[] | null;
  loading: boolean;
  appVersionId: string | null;
  appVersion: string | null;
  appFeatureType: string | null;
  appFeatureId: number | null;
  columnType: string | null;
  columnId: string | null;
  /** monday's `user.isViewOnly`; viewers cannot use SIMB on any surface. */
  isViewOnly: boolean;
  /** monday's permissions.approvedScopes: the scopes this account has granted. */
  approvedScopes: string[];
  /** monday's permissions.requiredScopes: the scopes the current app version needs. */
  requiredScopes: string[];
}

export const fetchMondayContext = createAsyncThunk(
  "monday/fetchContext",
  (_, { rejectWithValue }) => {
    return monday
      .get("context")
      .then((response) => {
        const appFeatureType = response?.data?.appFeature?.type;

        let itemIdsArray = [];

        appFeatureType === "AppFeatureItemBatchAction"
          ? (itemIdsArray = response.data.selectedPulsesIds)
          : itemIdsArray.push(response.data.itemId);

        return {
          userId: response?.data?.user?.id,
          accountId: response?.data?.account?.id,
          currentBoard: response?.data?.boardId,
          itemIds: itemIdsArray,
          appVersionId: response?.data?.appVersion?.id,
          appVersion: response?.data?.appVersion?.versionData?.displayNumber,
          appFeatureType: appFeatureType,
          appFeatureId: response?.data?.appFeatureId,
          // Column-extension features nest the column under `column`; a column
          // feature's dialog (AppFeatureDialog) reports `columnId`/`columnType`
          // at the top level of the context instead.
          columnType: response?.data?.column?.type ?? response?.data?.columnType ?? null,
          columnId: response?.data?.column?.id ?? response?.data?.columnId ?? null,
          isViewOnly: !!response?.data?.user?.isViewOnly,
          approvedScopes: response?.data?.permissions?.approvedScopes ?? [],
          requiredScopes: response?.data?.permissions?.requiredScopes ?? [],
        };
      })
      .catch((error) => {
        // If an error occurs, reject the promise and handle it in the Redux slice
        return rejectWithValue(error.message);
      });
  },
);

const initialState: MondayState = {
  userId: null,
  accountId: null,
  currentBoard: null,
  itemIds: null,
  loading: false,
  appVersionId: null,
  appVersion: null,
  appFeatureType: null,
  appFeatureId: null,
  columnType: null,
  columnId: null,
  isViewOnly: false,
  approvedScopes: [],
  requiredScopes: [],
};

const mondaySlice = createSlice({
  name: "monday",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMondayContext.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMondayContext.fulfilled, (state, action) => {
        state.loading = false;
        state.userId = action.payload.userId;
        state.accountId = action.payload.accountId;
        state.currentBoard = action.payload.currentBoard;
        state.itemIds = action.payload.itemIds;
        state.appVersionId = action.payload.appVersionId;
        state.appVersion = action.payload.appVersion;
        state.appFeatureType = action.payload.appFeatureType;
        state.columnType = action.payload.columnType;
        state.columnId = action.payload.columnId;
        state.isViewOnly = action.payload.isViewOnly;
        state.approvedScopes = action.payload.approvedScopes;
        state.requiredScopes = action.payload.requiredScopes;
      })
      .addCase(fetchMondayContext.rejected, (state) => {
        state.loading = false;
      });
  },
});

export default mondaySlice.reducer;
